import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, RefreshCw, Users, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, ExternalLink, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

function StatusBadge({ status }) {
  const styles = {
    test_success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    sent: 'bg-blue-100 text-blue-800 border-blue-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
    failed: 'bg-orange-100 text-orange-800 border-orange-200',
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    skipped: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  const labels = {
    test_success: 'Test OK',
    sent: 'Sent',
    rejected: 'Rejected',
    failed: 'Failed',
    pending: 'Pending',
    skipped: 'Skipped',
  };
  const cls = styles[status] || styles.pending;
  const label = labels[status] || status;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full border ${cls}`} data-testid={`status-badge-${status}`}>
      {label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-warm-200 rounded-xl p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-warm-950">{value}</p>
        <p className="text-xs text-warm-500">{label}</p>
      </div>
    </div>
  );
}

function LeadRow({ lead, isExpanded, onToggle }) {
  const createdDate = lead.created_at
    ? new Date(lead.created_at).toLocaleDateString('en-CA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  return (
    <>
      <tr
        className="border-b border-warm-100 hover:bg-warm-50/50 cursor-pointer transition-colors"
        onClick={onToggle}
        data-testid={`lead-row-${lead.id}`}
      >
        <td className="px-3 py-3 text-sm font-medium text-warm-900">
          {lead.first_name} {lead.last_name}
        </td>
        <td className="px-3 py-3 text-sm text-warm-600">{lead.email}</td>
        <td className="px-3 py-3 text-sm text-warm-600">{lead.phone}</td>
        <td className="px-3 py-3 text-sm text-warm-600">{lead.province_full || lead.province}</td>
        <td className="px-3 py-3">
          <StatusBadge status={lead.leadbyte_status} />
        </td>
        <td className="px-3 py-3 text-xs text-warm-400">{createdDate}</td>
        <td className="px-3 py-3 text-warm-400">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-warm-50/80">
          <td colSpan={7} className="px-4 py-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-warm-400 uppercase tracking-wide mb-0.5">Gender</p>
                <p className="text-warm-800 font-medium">{lead.gender}</p>
              </div>
              <div>
                <p className="text-warm-400 uppercase tracking-wide mb-0.5">Date of Birth</p>
                <p className="text-warm-800 font-medium">{lead.date_of_birth}</p>
              </div>
              <div>
                <p className="text-warm-400 uppercase tracking-wide mb-0.5">Smoker</p>
                <p className="text-warm-800 font-medium">{lead.smoker === 'no' ? 'Non-smoker' : 'Smoker'}</p>
              </div>
              <div>
                <p className="text-warm-400 uppercase tracking-wide mb-0.5">Lead ID</p>
                <p className="text-warm-800 font-medium font-mono text-[10px]">{lead.id}</p>
              </div>
              {lead.leadbyte_response && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-warm-400 uppercase tracking-wide mb-0.5">LeadByte Response</p>
                  <pre className="text-[10px] text-warm-600 bg-warm-100 rounded-lg p-2 overflow-x-auto">
                    {JSON.stringify(lead.leadbyte_response, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [lbMode, setLbMode] = useState('yes');
  const [modeLoading, setModeLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`);
      const data = Array.isArray(res.data) ? res.data : [];
      const sorted = data.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setLeads(sorted);
    } catch (e) {
      console.error('Failed to fetch leads:', e);
    }
    setLoading(false);
  }, []);

  const fetchMode = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/leadbyte-mode`);
      setLbMode(res.data.mode);
    } catch (e) {
      console.error('Failed to fetch mode:', e);
    }
  }, []);

  const toggleMode = async () => {
    const newMode = lbMode === 'yes' ? 'no' : 'yes';
    if (newMode === 'no' && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    setShowConfirm(false);
    setModeLoading(true);
    try {
      const res = await axios.post(`${API}/settings/leadbyte-mode`, { mode: newMode });
      setLbMode(res.data.mode);
    } catch (e) {
      console.error('Failed to toggle mode:', e);
    }
    setModeLoading(false);
  };

  useEffect(() => {
    if (authenticated) {
      fetchLeads();
      fetchMode();
    }
  }, [authenticated, fetchLeads, fetchMode]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
    }
  };

  // Stats
  const totalLeads = leads.length;
  const successLeads = leads.filter(l => l.leadbyte_status === 'test_success' || l.leadbyte_status === 'sent').length;
  const rejectedLeads = leads.filter(l => l.leadbyte_status === 'rejected').length;
  const pendingLeads = leads.filter(l => l.leadbyte_status === 'pending' || l.leadbyte_status === 'skipped').length;

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center px-4" data-testid="admin-login-page">
        <div className="w-full max-w-sm">
          <div className="bg-white border border-warm-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-center mb-5">
              <div className="w-12 h-12 bg-warm-950 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" />
              </div>
            </div>
            <h1 className="text-xl font-bold text-warm-950 text-center mb-1">Admin Dashboard</h1>
            <p className="text-xs text-warm-500 text-center mb-5">Enter password to view leads</p>

            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                placeholder="Password"
                data-testid="admin-password-input"
                className="w-full h-11 px-3 border border-warm-200 rounded-lg text-sm text-warm-900 placeholder:text-warm-400 focus:border-warm-600 focus:ring-2 focus:ring-warm-200 focus:outline-none mb-3"
                autoFocus
              />
              {passwordError && (
                <p className="text-red-600 text-xs mb-3 flex items-center gap-1" data-testid="admin-password-error">
                  <XCircle className="w-3 h-3" /> {passwordError}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-btn"
                className="w-full h-11 bg-warm-950 text-white font-semibold rounded-lg hover:bg-warm-800 transition-colors text-sm"
              >
                Access Dashboard
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-warm-950 border-b border-warm-800">
        <div className="container mx-auto px-4 max-w-6xl py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white">LPC2 — Lead Dashboard</h1>
            <p className="text-[11px] text-warm-400">Canada Care Lead Management</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="text-xs text-warm-400 hover:text-white transition-colors flex items-center gap-1"
              data-testid="back-to-site-link"
            >
              <ExternalLink className="w-3 h-3" /> Landing Page
            </a>
            <button
              onClick={fetchLeads}
              disabled={loading}
              data-testid="refresh-leads-btn"
              className="h-9 px-3 bg-warm-800 text-white text-xs font-semibold rounded-lg hover:bg-warm-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 max-w-6xl py-6">
        {/* LeadByte Mode Toggle */}
        <div className={`mb-4 border rounded-xl p-4 flex items-center justify-between ${lbMode === 'no' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="leadbyte-mode-toggle">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${lbMode === 'no' ? 'bg-emerald-600' : 'bg-amber-500'}`}>
              {lbMode === 'no' ? <CheckCircle className="w-5 h-5 text-white" /> : <AlertTriangle className="w-5 h-5 text-white" />}
            </div>
            <div>
              <p className={`text-sm font-bold ${lbMode === 'no' ? 'text-emerald-900' : 'text-amber-900'}`}>
                LeadByte: {lbMode === 'no' ? 'LIVE' : 'TEST MODE'}
              </p>
              <p className={`text-[11px] ${lbMode === 'no' ? 'text-emerald-600' : 'text-amber-600'}`}>
                {lbMode === 'no' ? 'Leads are being distributed to agents' : 'Leads are accepted but NOT distributed'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {showConfirm && (
              <div className="flex items-center gap-2 mr-2">
                <p className="text-xs text-red-600 font-semibold">Go live? Leads will be sent to agents.</p>
                <button
                  onClick={toggleMode}
                  data-testid="confirm-live-btn"
                  className="h-8 px-3 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="h-8 px-3 bg-warm-200 text-warm-700 text-xs font-semibold rounded-lg hover:bg-warm-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
            {!showConfirm && (
              <button
                onClick={toggleMode}
                disabled={modeLoading}
                data-testid="toggle-mode-btn"
                className={`h-9 px-4 text-xs font-bold rounded-lg transition-colors disabled:opacity-50 ${
                  lbMode === 'no'
                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {modeLoading ? 'Switching...' : lbMode === 'no' ? 'Switch to Test' : 'Go Live'}
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="Total Leads" value={totalLeads} color="bg-warm-700" />
          <StatCard icon={CheckCircle} label="Successful" value={successLeads} color="bg-emerald-600" />
          <StatCard icon={XCircle} label="Rejected" value={rejectedLeads} color="bg-red-500" />
          <StatCard icon={Clock} label="Pending" value={pendingLeads} color="bg-yellow-500" />
        </div>

        {/* Table */}
        <div className="bg-white border border-warm-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-warm-100 flex items-center justify-between">
            <h2 className="font-bold text-warm-950 text-sm">All Leads</h2>
            <p className="text-xs text-warm-400">{totalLeads} total</p>
          </div>

          {leads.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Users className="w-10 h-10 text-warm-300 mx-auto mb-3" />
              <p className="text-sm text-warm-500">No leads yet</p>
              <p className="text-xs text-warm-400">Leads will appear here when people submit the form</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="bg-warm-50 border-b border-warm-200">
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-warm-500 uppercase tracking-wide">Name</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-warm-500 uppercase tracking-wide">Email</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-warm-500 uppercase tracking-wide">Phone</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-warm-500 uppercase tracking-wide">Province</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-warm-500 uppercase tracking-wide">LeadByte</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-warm-500 uppercase tracking-wide">Date</th>
                    <th className="px-3 py-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <LeadRow
                      key={lead.id}
                      lead={lead}
                      isExpanded={expandedId === lead.id}
                      onToggle={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
